import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Search, Check } from 'lucide-react';
import { cn } from '../../lib/utils';
import { getRestaurantColor } from '../../utils/restaurantColors';

// Re-exported for backwards compatibility; the palette + per-restaurant colour now live in
// utils/restaurantColors.ts (single source of truth, keyed by restaurant id).
export { RESTAURANT_COLORS } from '../../utils/restaurantColors';

interface RestaurantFilterDropdownProps {
  /** The restaurants to offer. Colours are assigned deterministically per restaurant id. */
  restaurants: { id: string; name: string }[];
  /**
   * The shared restaurant filter. Empty = ALL restaurants selected (no active filter).
   * Toggling here narrows the page; selecting everyone collapses back to empty.
   */
  selectedRestaurantIds: string[];
  setSelectedRestaurantIds: (ids: string[]) => void;
  /** Hide the small colour swatch next to each name (e.g. where colours aren't meaningful). */
  hideColorSwatch?: boolean;
  className?: string;
}

/**
 * Searchable multi-select "Select restaurants" dropdown with green checkboxes.
 * Self-contained: it owns the empty-means-all convention, the toggle guard (you can never
 * land on zero — that would read as "all"), and the "Select all" reset. Shared by the revenue
 * trend graph and the dashboard so the all-restaurants filter looks/behaves identically.
 */
export const RestaurantFilterDropdown: React.FC<RestaurantFilterDropdownProps> = ({
  restaurants,
  selectedRestaurantIds,
  setSelectedRestaurantIds,
  hideColorSwatch = false,
  className,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const allIds = useMemo(() => restaurants.map((r) => r.id), [restaurants]);

  // Empty selection = ALL restaurants selected.
  const effectiveSelected = selectedRestaurantIds.length > 0 ? selectedRestaurantIds : allIds;

  const colorById = useMemo(() => {
    const map: Record<string, string> = {};
    restaurants.forEach((r) => {
      map[r.id] = getRestaurantColor(r.id);
    });
    return map;
  }, [restaurants]);

  const toggle = (id: string) => {
    const next = effectiveSelected.includes(id)
      ? effectiveSelected.filter((x) => x !== id)
      : [...effectiveSelected, id];
    // Keep at least one restaurant selected (empty would otherwise read as "all").
    if (next.length === 0) return;
    // All selected → store empty so it reads as "no active filter".
    setSelectedRestaurantIds(next.length === allIds.length ? [] : next);
  };

  // "Select all" clears the per-restaurant filter (empty = all restaurants).
  const selectAll = () => setSelectedRestaurantIds([]);

  const filtered = restaurants.filter((r) =>
    r.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div ref={ref} className={cn('w-full sm:w-fit relative', className)}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full sm:w-fit flex items-center justify-between gap-2 border border-borderPrimary dark:border-borderPrimary-dark rounded-xl px-3 h-11 bg-white dark:bg-card-dark hover:bg-gray-50 dark:hover:bg-white/5 transition shadow-sm"
      >
        <span className="text-sm font-semibold text-textSecondary dark:text-textSecondary-dark whitespace-nowrap">
          {t('revenueHistory.selectRestaurants')}
        </span>
        <ChevronDown
          className={`w-5 h-5 text-textSecondary dark:text-textSecondary-dark transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div className="absolute right-0 top-[calc(100%+4px)] z-30 bg-white dark:bg-card-dark border border-borderPrimary dark:border-borderPrimary-dark rounded-xl shadow-xl w-full sm:w-[320px] overflow-hidden">
          <div className="px-4 pt-4">
            <div className="flex items-center justify-between gap-2 mb-3">
              <p className="text-base font-semibold text-textPrimary dark:text-textPrimary-dark">
                {t('revenueHistory.selectRestaurants')}
              </p>
              <button
                type="button"
                onClick={selectAll}
                className="text-sm font-semibold text-[#0E8965] dark:text-darkGreenColor-dark hover:underline"
              >
                {t('revenueHistory.selectAll')}
              </button>
            </div>
            <div className="flex items-center gap-1.5 border border-borderPrimary dark:border-borderPrimary-dark rounded-xl px-3 h-11 mb-3">
              <Search className="w-5 h-5 text-textSecondary dark:text-textSecondary-dark flex-shrink-0" />
              <input
                className="text-sm font-medium outline-none flex-1 bg-transparent dark:bg-transparent text-textPrimary dark:text-textPrimary-dark placeholder:text-textTertiary"
                placeholder={t('revenueHistory.searchRestaurants')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
          <div className="overflow-y-auto px-3 pb-3 space-y-1" style={{ maxHeight: 280 }}>
            {filtered.map((r) => {
              const isChecked = effectiveSelected.includes(r.id);
              return (
                <button
                  type="button"
                  key={r.id}
                  onClick={() => toggle(r.id)}
                  className={
                    'w-full flex items-center gap-2.5 py-2 px-2.5 text-left rounded-lg transition ' +
                    (isChecked ? 'bg-[#0E89651A]' : 'hover:bg-gray-50 dark:hover:bg-white/5')
                  }
                >
                  <span
                    className={
                      'w-5 h-5 rounded-md flex items-center justify-center border flex-shrink-0 ' +
                      (isChecked
                        ? 'bg-[#0E8965] dark:bg-darkGreenColor-dark border-[#0E8965] dark:border-darkGreenColor-dark'
                        : 'border-borderPrimary dark:border-borderPrimary-dark')
                    }
                  >
                    {isChecked && <Check className="w-3.5 h-3.5 text-white dark:text-white" />}
                  </span>
                  {!hideColorSwatch && (
                    <span className="w-2.5 h-2.5 rounded-[3px] flex-shrink-0" style={{ backgroundColor: colorById[r.id] }} />
                  )}
                  <span className="text-sm font-medium text-textPrimary dark:text-textPrimary-dark truncate">
                    {r.name}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default RestaurantFilterDropdown;
